'use client'

import { useState, useEffect, useCallback } from 'react';
import { ConnectWalletStep } from './ConnectWalletStep';
import { EmailVerificationForm } from './EmailVerificationForm';
import { SelectCryptoStep } from './SelectCryptoStep';
import { PurchaseStep } from './PurchaseStep';
import styles from '../styles/PurchaseFlow.module.css';
import { useAccount } from 'wagmi';
import { ReferralCodeInput } from './ReferralCodeInput';
import { useAuth } from '../../context/AuthContext';

// Update CryptoCurrency type to include SOL for Solana network
// And ETH_BASE for Base network
type CryptoCurrency = 'ETH' | 'USDT' | 'USDC' | 'MATIC' | 'SOL' | 'ETH_BASE' | 'USDC_BASE';

interface PurchaseFlowProps {
  onClose?: () => void;
  initialStep?: number;
  onStepChange?: (step: number, totalSteps: number) => void;
}

export const PurchaseFlow = ({ onClose, initialStep = 1, onStepChange }: PurchaseFlowProps) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [transactionHash, setTransactionHash] = useState<string | null>(null);
  const [tokenAmount, setTokenAmount] = useState<number | null>(null);
  const [emailAddress, setEmailAddress] = useState<string | null>(null);
  const [selectedCryptoForPurchase, setSelectedCryptoForPurchase] = useState<CryptoCurrency | null>(null);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  
  const { address: walletAddress } = useAccount();
  const { user } = useAuth();
  
  const totalSteps = 6;

  const updateStep = useCallback((newStep: number) => {
    const clampedStep = Math.max(1, Math.min(newStep, totalSteps));
    setCurrentStep(clampedStep);
    if (onStepChange) {
      onStepChange(clampedStep, totalSteps);
    }
  }, [onStepChange, totalSteps]);

  const nextStep = useCallback(() => {
    updateStep(currentStep + 1);
  }, [currentStep, updateStep]);

  useEffect(() => {
    // If user is verified, skip email verification step
    if (user && user.verifiedEmail && currentStep === 2) {
      console.log('PurchaseFlow: User is verified in auth context, skipping verification step');
      setEmailAddress(user.email);
      nextStep();
    } else if (currentStep === 2 && walletAddress) { // Ensure walletAddress is available
      // Also check localStorage for verified email associated with the CURRENT wallet address
      const storedEmailKey = `gam_verified_email_${walletAddress}`;
      const storedVerifiedKey = `gam_email_verified_${walletAddress}`;
      
      const storedEmail = localStorage.getItem(storedEmailKey);
      const storedVerified = localStorage.getItem(storedVerifiedKey);
      
      if (storedEmail && storedVerified === 'true') {
        console.log(`PurchaseFlow: Found verified email in localStorage for ${walletAddress}, skipping verification step`);
        setEmailAddress(storedEmail);
        nextStep();
      } else {
        console.log(`PurchaseFlow: Email not verified for ${walletAddress}, showing verification step`, { 
          user, 
          isAuthenticated: !!user, 
          verifiedEmail: user?.verifiedEmail,
          checkedLocalStorage: true
        });
      }
    } else if (currentStep === 2 && !walletAddress) {
      // Handle case where wallet is not yet connected when reaching step 2 (should ideally not happen often)
       console.log('PurchaseFlow: Wallet address not available yet on step 2.');
    }
    
    // Check for stored referral code (this is fine as a global key)
    const storedReferralCode = localStorage.getItem('gam_referral_code');
    if (storedReferralCode) {
      setReferralCode(storedReferralCode);
    }
  }, [currentStep, nextStep, user, walletAddress]); // Add walletAddress to dependency array

  useEffect(() => {
    updateStep(initialStep);
    console.log('PurchaseFlow: Setting initial step to', initialStep);
  }, [initialStep, updateStep]);
  
  const prevStep = useCallback(() => {
    const next = Math.max(currentStep - 1, 1);
    if (currentStep === 5 && next === 4) {
        console.log('PurchaseFlow: Going back to crypto selection, resetting selected crypto.');
        setSelectedCryptoForPurchase(null);
    }
    updateStep(next);
  }, [currentStep, updateStep]);

  const handleCryptoSelected = useCallback((crypto: CryptoCurrency) => {
      console.log(`PurchaseFlow: Crypto selected: ${crypto}`);
      setSelectedCryptoForPurchase(crypto);
      nextStep();
  }, [nextStep]);
  
  const handleReferralCodeSubmit = useCallback((code: string | null) => {
    console.log(`PurchaseFlow: Referral code set: ${code}`);
    setReferralCode(code);
    if (code) {
      localStorage.setItem('gam_referral_code', code);
    }
    nextStep();
  }, [nextStep]);
  
  const handlePurchaseComplete = useCallback((txHash: string, tokens: number) => {
    console.log('PurchaseFlow: Purchase complete callback triggered');
    setTransactionHash(txHash);
    setTokenAmount(tokens);
    
    // Fetch email associated with the current wallet address
    const storedEmail = walletAddress ? localStorage.getItem(`gam_verified_email_${walletAddress}`) : null;
    if (storedEmail) {
      setEmailAddress(storedEmail);
    } else if (user?.email) {
      // Fallback to user context email if available
      setEmailAddress(user.email);
    }
    
    nextStep();
  }, [nextStep, walletAddress, user?.email]); // Add walletAddress and user.email as dependencies
  
  return (
    <div className={styles.purchaseFlowContainer}>
      
      <div className={styles.steps}>
        <div className={styles.step}>
          <div className={`${styles.stepCircle} ${currentStep >= 1 ? styles.active : ''} ${currentStep > 1 ? styles.completed : ''}`}>
            {currentStep > 1 ? '✓' : '1'}
          </div>
          <div className={`${styles.stepLabel} ${currentStep >= 1 ? styles.active : ''} ${currentStep > 1 ? styles.completed : ''}`}>
            Connect Wallet
          </div>
        </div>
        
        <div className={styles.step}>
          <div className={`${styles.stepCircle} ${currentStep >= 2 ? styles.active : ''} ${currentStep > 2 ? styles.completed : ''}`}>
            {currentStep > 2 ? '✓' : '2'}
          </div>
          <div className={`${styles.stepLabel} ${currentStep >= 2 ? styles.active : ''} ${currentStep > 2 ? styles.completed : ''}`}>
            Verify Email
          </div>
        </div>
        
        <div className={styles.step}>
          <div className={`${styles.stepCircle} ${currentStep >= 3 ? styles.active : ''} ${currentStep > 3 ? styles.completed : ''}`}>
            {currentStep > 3 ? '✓' : '3'}
          </div>
          <div className={`${styles.stepLabel} ${currentStep >= 3 ? styles.active : ''} ${currentStep > 3 ? styles.completed : ''}`}>
            Referral Code
          </div>
        </div>
        
        <div className={styles.step}>
          <div className={`${styles.stepCircle} ${currentStep >= 4 ? styles.active : ''} ${currentStep > 4 ? styles.completed : ''}`}>
            {currentStep > 4 ? '✓' : '4'}
          </div>
          <div className={`${styles.stepLabel} ${currentStep >= 4 ? styles.active : ''} ${currentStep > 4 ? styles.completed : ''}`}>
            Select Crypto
          </div>
        </div>
        
        <div className={styles.step}>
          <div className={`${styles.stepCircle} ${currentStep >= 5 ? styles.active : ''} ${currentStep > 5 ? styles.completed : ''}`}>
            {currentStep > 5 ? '✓' : '5'}
          </div>
          <div className={`${styles.stepLabel} ${currentStep >= 5 ? styles.active : ''} ${currentStep > 5 ? styles.completed : ''}`}>
            Buy
          </div>
        </div>
        
        <div className={styles.step}>
          <div className={`${styles.stepCircle} ${currentStep >= 6 ? styles.active : ''}`}>
            6
          </div>
          <div className={`${styles.stepLabel} ${currentStep >= 6 ? styles.active : ''}`}>
            Success
          </div>
        </div>
      </div>
      
      <div className={styles.activeStepContainer}>
        {currentStep === 1 && (
          <ConnectWalletStep onNext={nextStep} />
        )}
        
        {currentStep === 2 && (
          <>
            <EmailVerificationForm onPrevious={prevStep} onNext={nextStep} />
          </>
        )}
        
        {currentStep === 3 && (
          <ReferralCodeInput 
            onPrevious={prevStep} 
            onNext={handleReferralCodeSubmit}
            initialValue={referralCode}
          />
        )}
        
        {currentStep === 4 && (
          <SelectCryptoStep 
              onPrevious={prevStep} 
              onCryptoSelected={handleCryptoSelected} 
          />
        )}

        {currentStep === 5 && selectedCryptoForPurchase && (
          <PurchaseStep 
              selectedCrypto={selectedCryptoForPurchase}
              referralCode={referralCode}
              onPrevious={prevStep} 
              onNext={handlePurchaseComplete}
              userEmail={user?.email || emailAddress}
          />
        )}
        
        {currentStep === 5 && !selectedCryptoForPurchase && (
            <div className={styles.errorBox}>Please go back and select a cryptocurrency.</div>
        )}

        {currentStep === 6 && (
          <div className={styles.transactionConfirmed}>
            <div className={styles.iconContainerSuccess}>
              <svg className={styles.checkmarkIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                <circle className={styles.checkmarkCircle} cx="26" cy="26" r="25" fill="none"/>
                <path className={styles.checkmarkCheck} fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
              </svg>
            </div>
            <h3 className={styles.successTitle}>Purchase Confirmed!</h3>
            <p className={styles.successMessage}>
              Your purchase of <strong className={styles.highlight}>{tokenAmount?.toLocaleString(undefined, { maximumFractionDigits: 2 })} GAM</strong> tokens has been successfully recorded.
            </p>
            
            <div className={styles.orderDetailsSection}>
              <h4 className={styles.orderDetailsTitle}>Key Information:</h4>
              <ul className={styles.orderDetailsList}>
                <li>Your GAM tokens are <strong className={styles.highlight}>NOT</strong> in your wallet yet.</li>
                <li>Tokens will be minted and available for claim after the presale concludes.</li>
                <li>You will receive an email confirmation shortly at <strong className={styles.highlight}>{emailAddress || 'your verified email'}</strong>.</li>
                <li>We will also notify you by email when tokens are ready to be claimed.</li>
                <li>This purchase is securely linked to your wallet: <strong className={styles.highlightSmall}>{walletAddress?.slice(0, 6)}...{walletAddress?.slice(-4)}</strong></li>
                {referralCode && <li>Referral code applied: <strong className={styles.highlightSmall}>{referralCode}</strong></li>}
              </ul>
            </div>

            {transactionHash && (
              <div className={styles.transactionInfoContainer}>
                <p className={styles.transactionInfoLabel}>Transaction Reference:</p>
                <p className={styles.transactionHashValue}>{transactionHash.slice(0, 10)}...{transactionHash.slice(-10)}</p>
              </div>
            )}
            
            <p className={styles.supportText}>
              If you have any questions, please don&apos;t hesitate to contact our support team.
            </p>

            <button 
              className={`${styles.button} ${styles.primaryButton}`}
              onClick={onClose || (() => updateStep(1))} // updateStep(1) to reset flow
              style={{ marginTop: '1.5rem' }}
            >
              {onClose ? 'Close' : 'Make Another Purchase'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}; 